import { ReactElement } from 'react';
import { Wrapper } from '@components/common/Wrapper/wrapper';
import { useLocation } from 'react-router-dom';
import { useSiteLeadOverview } from '@services/hooks/site-summary-listing/useSiteLeadOverview';
import { TypeOfContractor } from '@components/competition-conversion/components/competitionConversionContract';
import { LeadInfoRow } from '@components/contractor360KAE/components/siteLeadOverviewKAE/leadInfoRow';
import { StatCardGroup } from '@components/common/statCardGroup';

export const SiteOverviewInfo = (): ReactElement => {
	const { state } = useLocation();
	const { data: siteOverview, isFetching, error } = useSiteLeadOverview(state?.siteId);

	const getProductUsedBy = (): string | null => {
		const isCompetition = siteOverview?.glue_used_at_the_site === 'Competition';
		const brand = isCompetition ? siteOverview?.brand_of_glue_used_at_site : siteOverview?.glue_used_at_the_site;
		const grade = siteOverview?.grade_glue_used_at_the_site;
		if (!brand && !grade) return null;

		return [brand, grade].filter(Boolean).join(' - ');
	};

	const getGluUsed = (): string | null => {
		return siteOverview?.visits?.length
			? siteOverview.visits[siteOverview.visits.length - 1]?.fv_glue_used_so_far
			: '-';
	};

	const getFvGlueUsedSoFar = (): string | null => {
		return siteOverview?.visits?.length
			? siteOverview.visits[siteOverview.visits.length - 1]?.competition_glue_used_so_far
			: '-';
	};
	const fvGlueUsedSoFar = getGluUsed();
	const competitionGlueUsedSoFar = getFvGlueUsedSoFar();
	const glueUsed = getProductUsedBy();

	return (
		<>
			<div className="site-user-info">
				<Wrapper isLoaderVisible={isFetching} isError={error}>
					<div className="container">
						<div className="overview-heading">
							<div className="left-content">
								<h1 className="h3">{siteOverview?.name}</h1>
								<span className="site-id">Site ID: {siteOverview?.id}</span>
							</div>

							<span className="visited-by">Visited by: {siteOverview?.visited_by}</span>
						</div>
						<div className="site-address-wrapper">
							<span className="common-title">Site Address</span>

							<div className="address-wrapper">
								<address>{siteOverview?.site_location_c}</address>
								<span aria-label="address link" className="location">
									<span className="icon-map-pin"></span>
								</span>
							</div>
						</div>

						{siteOverview && (
							<StatCardGroup
								items={[
									{ label: 'Site Potential', value: siteOverview.glue_potential, unit: 'KG' },
									{ label: 'FV Glue used so far', value: fvGlueUsedSoFar, unit: 'KG' },
									{ label: 'Competition glue used so far', value: competitionGlueUsedSoFar },
								]}
							/>
						)}
					</div>
				</Wrapper>
			</div>

			<div className="site-basic-info">
				<Wrapper isLoaderVisible={isFetching} isError={error}>
					<div className="container">
						{siteOverview?.roff_substrate_c && (
							<div className="substrates-wrapper">
								<span className="common-title">Substrates on site</span>
								<div className="chips-panel">
									{siteOverview?.roff_substrate_c
										.split(',')
										.map((substrate) => substrate.trim())
										.filter(Boolean)
										.map((substrate) => (
											<span key={substrate} className="status">
												{substrate}
											</span>
										))}
								</div>
							</div>
						)}

						<div className="lead-info-wrapper">
							<ul>
								<LeadInfoRow
									label="Type of Contract"
									value={TypeOfContractor.find((item) => item.value === siteOverview?.type_of_contract)?.name}
								/>
								<LeadInfoRow label="Reason for Competition" value={siteOverview?.reason_of_competition} />
								<LeadInfoRow label="Brand & Grade of Glue Used" value={glueUsed} />
							</ul>
						</div>
					</div>
				</Wrapper>
			</div>
		</>
	);
};
